import { useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { queryKeys } from './queryKeys';
import { authFetch } from '../../../CommonUtils/authFetch';
import { urlWithParams } from '../../../CommonUtils/urlWithParams';
import { UPDATE_APEX_ACCOUNT_STATUS } from '../StatusViews.meta';

interface AccountStatusDTO {
  ManagedAccountId: string;
  ApexAccountNumber: string;
  ApexStatus: string;
  OrionAccountId: string;
  ApexStatusView: string;
  IsClosed: boolean;
}

interface FetcherParams {
  clientId: string;
  accountId: string;
}

const fetchAccountStatus = async ({
  accountId,
  clientId,
}: FetcherParams): Promise<AccountStatusDTO> => {
  return authFetch(
    urlWithParams('/Consumer/ManagedAccounts/ApexAccountStatusSummary', {
      clientId,
      id: accountId,
    }),
  );
};

interface QueryParams {
  clientId: string;
  accountId: string;
}

export const useAccountStatusQuery = ({ clientId, accountId }: QueryParams) => {
  const dispatch = useDispatch();

  return useQuery(
    queryKeys.accountStatus(clientId, accountId),

    () => fetchAccountStatus({ clientId, accountId }),

    {
      onSuccess(data) {
        dispatch({
          type: UPDATE_APEX_ACCOUNT_STATUS,
          payload: {
            accountId,
            ...data,
          },
        });
      },

      cacheTime: 0,
      refetchInterval: 20_000,
    },
  );
};

interface CacheProps {
  clientId: string;
  accountId: string;
}

export const useAccountStatusCache = ({ clientId, accountId }: CacheProps) => {
  const queryClient = useQueryClient();

  const queryKey = queryKeys.accountStatus(clientId, accountId);

  const mutateStatusView = (statusView: string) => {
    queryClient.setQueryData<AccountStatusDTO | undefined>(queryKey, cache => {
      if (!cache) {
        return cache;
      }

      return {
        ...cache,
        ApexStatusView: statusView,
      };
    });
  };

  return {
    mutateStatusView,
  };
};
